/**
 * EF-CREW-PWA-NAVIGATION-AND-UTILITY-PAGES-V1
 * EF-FIX-CREW-ONBOARDING-FLOW-END-TO-END-V1
 * EF-UNIFY-CREW-DOCUMENT-UI-ONBOARDING-AND-PORTAL-V1
 * EF-CREW-ONBOARDING-STATUS-AND-DOCUMENT-APPROVAL-V1
 * EF-UNIFY-CREW-APP-AND-ONBOARDING-STATE-V1
 * 
 * Unified Crew Portal - Single entry point for all crew members
 * Combines profile management, document uploads, shifts, and settings
 * into a cohesive PWA experience with hamburger navigation.
 * 
 * Features:
 * - Hamburger navigation with slide-out menu
 * - Shifts & Calendar as primary views
 * - Profile & Documents as secondary sections
 * - Install App guide and About page
 * - Theme toggle (light/dark)
 */
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Alert, AlertTitle, AlertDescription } from "../components/ui/alert";
import { Switch } from "../components/ui/switch";
import { ScrollArea } from "../components/ui/scroll-area";
import { toast } from "sonner";
import { 
  User, 
  FileText, 
  LogOut, 
  Loader2,
  Building,
  Mail,
  Phone,
  CheckCircle,
  AlertCircle,
  Clock,
  XCircle,
  Info,
  Calendar,
  Briefcase,
  Lock,
  Menu,
  X,
  Download,
  Settings,
  Sun,
  Moon,
  Smartphone,
  Share,
  MoreVertical,
  ChevronRight,
  Home,
  // EF-CREW-PWA-COMPLETE-PROFILE-TABS-AND-THEME-FIX-V1: Additional icons
  MapPin,
  Flag,
  Shield,
  Heart,
  Car,
  CreditCard,
  FileCheck,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

// EF-UNIFY-CREW-DOCUMENT-UI-ONBOARDING-AND-PORTAL-V1: Use shared document component
import { CrewDocumentsPanel } from "../components/crew/CrewDocumentsPanel";
import CrewSelfDocumentsPanel from "../components/crew/CrewSelfDocumentsPanel";

// EF-CREW-PWA-PHASE-1-SHIFT-INTERACTION-V1: Import shift list component
import { CrewShiftList } from "../components/crew/CrewShiftList";

// EF-CREW-CALENDAR-VIEW-V1: Import calendar view component
import { CrewCalendarView } from "../components/crew/CrewCalendarView";

// EF-API-ENDPOINT-CONSISTENCY-GUARDRAIL-V1: Use centralized endpoints
import { CREW_ENDPOINTS, STAFF_ENDPOINTS } from "../api/endpoints";
// EF-SETTINGS-CONSOLIDATION-V1 (PHASE 3): Use centralized version info
import { BUILD_VERSION, APP_VERSION, BUILD_DATE, BUILD_COMMIT } from "../lib/version";
// EVENTSFLO-CREW-PWA-ACTIVE-ROUTE-AND-UI-SPLIT-AUDIT-FIX-V1
import { checkForUpdates as swCheckForUpdates } from "../lib/registerSw";
import api from "../lib/api";
// EVENTSFLO-CREW-PORTAL-WITHDRAW-AND-MESSAGES-V1
import {
  getCrewMessages,
  getCrewMessagesUnreadCount,
  markCrewMessageRead,
  markAllCrewMessagesRead,
} from "../lib/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { RefreshCw, Send, MessageSquare, Ban, CheckCheck, BellRing } from "lucide-react";

// EVENTSFLO-PORTAL-I18N-LANGUAGE-SELECTOR-PHASE1
import { useI18n } from "../i18n/I18nProvider";
import { LanguageSelector } from "../i18n/LanguageSelector";
// EVENTSFLO-CREW-PORTAL-I18N-PERSIST-PREFERENCE-V1
import i18n from "../i18n";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

/**
 * EF-CREW-PWA-COMPLETE-PROFILE-TABS-AND-THEME-FIX-V1: Profile field display component
 * Read-only display with label → value format
 */
function ProfileField({ icon: Icon, label, value }) {
  if (!value && value !== 0) return null;
  
  return (
    <div className="flex items-start gap-2 py-1">
      {Icon && <Icon className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />}
      <div className={Icon ? "" : "ml-6"}>
        <span className="text-xs text-muted-foreground block">{label}</span>
        <span className="text-sm">{value}</span>
      </div>
    </div>
  );
}

// EF-SETTINGS-CONSOLIDATION-V1 (PHASE 3): App version info from centralized source
const getAppInfo = (companyName) => ({
  name: companyName || "EventsFlo",
  version: APP_VERSION,
  buildVersion: BUILD_VERSION,
  buildDate: BUILD_DATE,
  environment: process.env.NODE_ENV === "production" ? "Production" : "Preview",
});

/**
 * EF-UNIFY-CREW-APP-AND-ONBOARDING-STATE-V1: Determine onboarding completion state
 * EVENTSFLO-CREW-PWA-ONBOARDING-REMINDER-AND-ADMIN-PENDING-DOC-ALERTS-V1:
 * Also handles the new `/api/crew/me/onboarding-status` shape (`is_complete: bool`).
 */
function getOnboardingState(status) {
  if (!status) return { isComplete: true, needsDocuments: false, needsProfile: false, hasRejected: false };
  
  const missingDocs = status.missing_required_documents?.length || 0;
  const rejectedDocs = status.rejected_documents?.length || 0;
  const pendingDocs = status.document_summary?.pending || 0;
  const needsProfile = status.status_type === "warning" && status.status_message?.toLowerCase().includes("profile");
  // V2 endpoint surfaces `is_complete` directly. Candidate endpoint uses `status_type === "success"`.
  const explicitComplete = typeof status.is_complete === "boolean"
    ? status.is_complete
    : (status.status_type === "success");
  
  return {
    isComplete: explicitComplete || (missingDocs === 0 && rejectedDocs === 0 && pendingDocs === 0),
    needsDocuments: missingDocs > 0,
    needsProfile: needsProfile,
    hasRejected: rejectedDocs > 0,
    hasPending: pendingDocs > 0,
  };
}

/**
 * EF-CREW-PWA-NAVIGATION-AND-UTILITY-PAGES-V1: Slide-out Navigation Menu
 * EF-PWA-BRANDING-V1-STEP4: Dynamic branding - uses companyInfo from context
 */
function NavigationMenu({ isOpen, onClose, activeView, onNavigate, onboardingComplete, onLogout, companyInfo, unreadMessages = 0 }) {
  const { t } = useI18n();
  if (!isOpen) return null;
  
  const menuItems = [
    { id: "shifts", label: t("crewPortal.nav.shifts"), icon: Briefcase, primary: true, requiresOnboarding: true },
    { id: "calendar", label: t("crewPortal.nav.calendar"), icon: Calendar, primary: true, requiresOnboarding: true },
    // EVENTSFLO-CREW-PORTAL-WITHDRAW-AND-MESSAGES-V1
    { id: "messages", label: t("crewPortal.nav.messages"), icon: MessageSquare, primary: true, badge: unreadMessages },
    { id: "profile", label: t("crewPortal.nav.profile"), icon: User, primary: false },
    { id: "documents", label: t("crewPortal.nav.documents"), icon: FileText, primary: false },
    { id: "install", label: t("crewPortal.nav.installApp"), icon: Download, utility: true },
    { id: "about", label: t("crewPortal.nav.about"), icon: Info, utility: true },
  ];
  
  // EF-PWA-BRANDING-V1-STEP4: Dynamic company name
  const displayName = companyInfo?.company_name || "EventsFlo";
  
  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-40 transition-opacity"
        onClick={onClose}
        data-testid="nav-backdrop"
      />
      
      {/* Menu Panel */}
      <div 
        className="fixed left-0 top-0 bottom-0 w-72 bg-card border-r z-50 shadow-xl transform transition-transform"
        data-testid="nav-menu"
      >
        <div className="flex flex-col h-full">
          {/* Header - EF-PWA-BRANDING-V1-STEP4: Dynamic branding */}
          <div className="p-4 border-b flex items-center justify-between">
            <div className="flex items-center gap-3">
              {companyInfo?.logo_url ? (
                <img 
                  src={companyInfo.logo_url} 
                  alt={displayName}
                  className="h-6 w-auto object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling && (e.target.nextSibling.style.display = 'block');
                  }}
                />
              ) : null}
              <Building className={`h-6 w-6 text-primary ${companyInfo?.logo_url ? 'hidden' : ''}`} />
              <span className="font-semibold text-lg">{displayName}</span>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-5 w-5" />
            </Button>
          </div>
          
          {/* Menu Items */}
          <ScrollArea className="flex-1 p-2">
            {/* Primary Section */}
            <div className="mb-4">
              <p className="text-xs font-semibold text-muted-foreground uppercase px-3 py-2">{t("crewPortal.sectionMain")}</p>
              {menuItems.filter(i => i.primary).map(item => {
                const isLocked = item.requiresOnboarding && !onboardingComplete;
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    onClick={() => !isLocked && onNavigate(item.id)}
                    disabled={isLocked}
                    className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${
                      activeView === item.id 
                        ? "bg-primary/10 text-primary" 
                        : isLocked 
                          ? "text-muted-foreground/50 cursor-not-allowed"
                          : "hover:bg-muted text-foreground"
                    }`}
                    data-testid={`nav-${item.id}`}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="flex-1 text-left">{item.label}</span>
                    {/* EVENTSFLO-CREW-PORTAL-WITHDRAW-AND-MESSAGES-V1: unread badge */}
                    {item.badge > 0 && (
                      <Badge
                        variant="default"
                        className="h-5 min-w-[1.25rem] px-1.5 bg-orange-500 hover:bg-orange-500 text-white text-[11px]"
                        data-testid={`nav-${item.id}-badge`}
                      >
                        {item.badge > 99 ? "99+" : item.badge}
                      </Badge>
                    )}
                    {isLocked && <Lock className="h-4 w-4" />}
                    {activeView === item.id && <ChevronRight className="h-4 w-4" />}
                  </button>
                );
              })}
            </div>
            
            {/* Secondary Section */}
            <div className="mb-4">
              <p className="text-xs font-semibold text-muted-foreground uppercase px-3 py-2">{t("crewPortal.sectionAccount")}</p>
              {menuItems.filter(i => !i.primary && !i.utility).map(item => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    onClick={() => onNavigate(item.id)}
                    className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${
                      activeView === item.id 
                        ? "bg-primary/10 text-primary" 
                        : "hover:bg-muted text-foreground"
                    }`}
                    data-testid={`nav-${item.id}`}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="flex-1 text-left">{item.label}</span>
                    {activeView === item.id && <ChevronRight className="h-4 w-4" />}
                  </button>
                );
              })}
            </div>
            
            {/* Utility Section */}
            <div className="mb-4">
              <p className="text-xs font-semibold text-muted-foreground uppercase px-3 py-2">{t("crewPortal.sectionSettings")}</p>
              {menuItems.filter(i => i.utility).map(item => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    onClick={() => onNavigate(item.id)}
                    className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${
                      activeView === item.id 
                        ? "bg-primary/10 text-primary" 
                        : "hover:bg-muted text-foreground"
                    }`}
                    data-testid={`nav-${item.id}`}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="flex-1 text-left">{item.label}</span>
                    {activeView === item.id && <ChevronRight className="h-4 w-4" />}
                  </button>
                );
              })}
            </div>
          </ScrollArea>
          
          {/* Footer */}
          <div className="p-4 border-t">
            <Button 
              variant="outline" 
              className="w-full justify-start text-red-500 hover:text-red-600 hover:bg-red-50"
              onClick={onLogout}
              data-testid="nav-logout"
            >
              <LogOut className="h-4 w-4 mr-2" />
              {t("crewPortal.signOut")}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * EF-CREW-PWA-NAVIGATION-AND-UTILITY-PAGES-V1: Install App Guide Page
 * EF-PWA-BRANDING-V1-STEP4: Dynamic branding
 */
function InstallAppView({ companyInfo }) {
  const { t } = useI18n();
  // EF-PWA-BRANDING-V1-STEP4: Dynamic company name
  const displayName = companyInfo?.company_name || "EventsFlo";
  
  return (
    <div className="space-y-6" data-testid="install-view">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
          <Smartphone className="h-8 w-8 text-primary" />
        </div>
        <h2 className="text-2xl font-bold">{t("crewPortal.installApp.headline", { appName: displayName })}</h2>
        <p className="text-muted-foreground mt-2">
          {t("crewPortal.installApp.subtitle")}
        </p>
      </div>
      
      {/* iPhone Instructions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="text-2xl">🍎</span>
            {t("crewPortal.installApp.iphoneTitle")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-semibold">1</div>
            <div>
              <p className="font-medium">{t("crewPortal.installApp.iphoneStep1Title")}</p>
              <p className="text-sm text-muted-foreground flex items-center gap-1">
                {t("crewPortal.installApp.iphoneStep1Hint")} <Share className="h-4 w-4" />
              </p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-semibold">2</div>
            <div>
              <p className="font-medium">{t("crewPortal.installApp.iphoneStep2Title")}</p>
              <p className="text-sm text-muted-foreground">{t("crewPortal.installApp.iphoneStep2Hint")}</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-semibold">3</div>
            <div>
              <p className="font-medium">{t("crewPortal.installApp.iphoneStep3Title")}</p>
              <p className="text-sm text-muted-foreground">{t("crewPortal.installApp.iphoneStep3Hint", { appName: displayName })}</p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Android Instructions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="text-2xl">🤖</span>
            {t("crewPortal.installApp.androidTitle")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-100 text-green-600 flex items-center justify-center font-semibold">1</div>
            <div>
              <p className="font-medium">{t("crewPortal.installApp.androidStep1Title")}</p>
              <p className="text-sm text-muted-foreground flex items-center gap-1">
                {t("crewPortal.installApp.androidStep1Hint")} <MoreVertical className="h-4 w-4" />
              </p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-100 text-green-600 flex items-center justify-center font-semibold">2</div>
            <div>
              <p className="font-medium">{t("crewPortal.installApp.androidStep2Title")}</p>
              <p className="text-sm text-muted-foreground">{t("crewPortal.installApp.androidStep2Hint")}</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-100 text-green-600 flex items-center justify-center font-semibold">3</div>
            <div>
              <p className="font-medium">{t("crewPortal.installApp.androidStep3Title")}</p>
              <p className="text-sm text-muted-foreground">{t("crewPortal.installApp.androidStep3Hint", { appName: displayName })}</p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Alert>
        <Info className="h-4 w-4" />
        <AlertTitle>{t("crewPortal.installApp.whyTitle")}</AlertTitle>
        <AlertDescription>
          {t("crewPortal.installApp.whyBody")}
        </AlertDescription>
      </Alert>
    </div>
  );
}

/**
 * EF-CREW-PWA-NAVIGATION-AND-UTILITY-PAGES-V1: About Page with Theme Toggle
 * EF-PWA-BRANDING-V1-STEP4: Dynamic branding
 * EF-SETTINGS-CONSOLIDATION-V1 (PHASE 3): Version from centralized source
 */
function AboutView({ theme, onThemeToggle, companyInfo }) {
  const { t } = useI18n();
  // EF-PWA-BRANDING-V1-STEP4: Dynamic app info
  const appInfo = getAppInfo(companyInfo?.company_name);
  // EVENTSFLO-CREW-PWA-ACTIVE-ROUTE-AND-UI-SPLIT-AUDIT-FIX-V1
  const [updating, setUpdating] = useState(false);

  const handleCheckForUpdates = async () => {
    setUpdating(true);
    try {
      const res = await swCheckForUpdates({ clearCaches: true });
      if (res.status === "update-activating") {
        toast.success(t("crewPortal.about.newVersionFound"));
      } else if (res.status === "reloaded") {
        /* reload already in progress */
      } else if (res.status === "unsupported") {
        toast.info(t("crewPortal.about.updateAutoInfo"));
        setTimeout(() => window.location.reload(), 600);
      } else if (res.status === "unable") {
        toast.error(t("crewPortal.about.updateUnable"));
      } else {
        toast.success(t("crewPortal.about.latestInstalled"));
      }
    } catch {
      toast.error(t("crewPortal.about.updateUnable"));
    } finally {
      setUpdating(false);
    }
  };

  const handleReloadApp = () => {
    try {
      if ("caches" in window) {
        caches.keys().then((keys) => Promise.all(keys.map((k) => caches.delete(k))));
      }
    } catch {
      /* ignore */
    }
    window.location.reload();
  };
  
  return (
    <div className="space-y-6" data-testid="about-view">
      {/* App Info Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="text-center mb-6">
            {/* EF-PWA-BRANDING-V1-STEP4: Show company logo or fallback */}
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-primary/10 mb-4">
              {companyInfo?.logo_url ? (
                <img 
                  src={companyInfo.logo_url}
                  alt={appInfo.name}
                  className="h-12 w-auto object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" class="h-10 w-10 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>';
                  }}
                />
              ) : (
                <Building className="h-10 w-10 text-primary" />
              )}
            </div>
            <h2 className="text-2xl font-bold">{appInfo.name}</h2>
            <p className="text-muted-foreground">{t("crewPortal.about.subtitle")}</p>
            {/* EF-SETTINGS-CONSOLIDATION-V1 (PHASE 3): Version badge */}
            <Badge variant="outline" className="mt-2" data-testid="pwa-version-badge">
              {appInfo.buildVersion}
            </Badge>
          </div>
          
          <div className="space-y-3 text-sm">
            <div className="flex justify-between py-2 border-b" data-testid="about-app-version">
              <span className="text-muted-foreground">{t("crewPortal.about.appVersion")}</span>
              <span className="font-medium font-mono">{appInfo.version}</span>
            </div>
            <div className="flex justify-between py-2 border-b" data-testid="about-build-version">
              <span className="text-muted-foreground">{t("crewPortal.about.buildVersion")}</span>
              <span className="font-medium font-mono">{appInfo.buildVersion}</span>
            </div>
            <div className="flex justify-between py-2 border-b" data-testid="about-build-date">
              <span className="text-muted-foreground">{t("crewPortal.about.buildDate")}</span>
              <span className="font-medium">{appInfo.buildDate}</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">{t("crewPortal.about.environment")}</span>
              <Badge variant={appInfo.environment === "Production" ? "default" : "secondary"}>
                {appInfo.environment === "Production"
                  ? t("crewPortal.about.envProduction")
                  : t("crewPortal.about.envPreview")}
              </Badge>
            </div>
          </div>

          {/* EVENTSFLO-CREW-PWA-ACTIVE-ROUTE-AND-UI-SPLIT-AUDIT-FIX-V1: always-visible update controls */}
          <div className="mt-4 flex gap-2">
            <Button
              type="button"
              variant="default"
              className="flex-1"
              onClick={handleCheckForUpdates}
              disabled={updating}
              data-testid="about-check-updates-btn"
            >
              {updating ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              {t("crewPortal.checkForUpdates")}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={handleReloadApp}
              data-testid="about-reload-app-btn"
            >
              {t("crewPortal.reloadApp")}
            </Button>
          </div>
          {BUILD_COMMIT && (
            <p className="mt-2 text-[11px] font-mono text-muted-foreground text-center">
              {t("crewPortal.about.commitLabel", { commit: BUILD_COMMIT })}
            </p>
          )}
        </CardContent>
      </Card>
      
      {/* Theme Toggle Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            {t("common.appearance")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {theme === "dark" ? (
                <Moon className="h-5 w-5 text-blue-400" />
              ) : (
                <Sun className="h-5 w-5 text-amber-500" />
              )}
              <div>
                <p className="font-medium">{t("common.darkMode")}</p>
                <p className="text-sm text-muted-foreground">
                  {theme === "dark" ? t("crewPortal.about.darkModeOnHint") : t("crewPortal.about.darkModeOffHint")}
                </p>
              </div>
            </div>
            <Switch
              checked={theme === "dark"}
              onCheckedChange={onThemeToggle}
              data-testid="theme-toggle"
            />
          </div>
        </CardContent>
      </Card>

      {/* EVENTSFLO-PORTAL-I18N-LANGUAGE-SELECTOR-PHASE1: Crew Portal language selector */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            {t("common.language")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <LanguageSelector
            variant="inline"
            testId="crew-language-selector"
          />
        </CardContent>
      </Card>
      
      {/* Support Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Info className="h-5 w-5" />
            {t("crewPortal.about.supportTitle")}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          <p>{t("crewPortal.about.supportBody")}</p>
          <p className="mt-2">
            {t("crewPortal.about.rightsReserved", { company: companyInfo?.company_name || "EventsFlo" })}
          </p>
          <p className="mt-1 text-xs">{t("crewPortal.about.poweredBy", { version: appInfo.buildVersion })}</p>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * EF-UNIFY-CREW-APP-AND-ONBOARDING-STATE-V1: Enhanced Onboarding Status Banner
 */
function OnboardingStatusBanner({ status, onboardingState }) {
  const { t } = useI18n();
  if (!status) return null;
  
  if (onboardingState.isComplete) {
    return (
      <Alert className="mb-6 bg-emerald-500/10 border-emerald-500/50 text-emerald-400" data-testid="onboarding-status-banner">
        <CheckCircle className="h-4 w-4 text-emerald-400" />
        <AlertTitle className="font-semibold">{t("crewPortal.onboarding.complete.title")}</AlertTitle>
        <AlertDescription className="mt-1">
          {t("crewPortal.onboarding.complete.body")}
        </AlertDescription>
      </Alert>
    );
  }
  
  const statusConfig = {
    error: { 
      icon: XCircle, 
      bg: "bg-red-500/10", 
      border: "border-red-500/50", 
      text: "text-red-400" 
    },
    warning: { 
      icon: AlertCircle, 
      bg: "bg-amber-500/10", 
      border: "border-amber-500/50", 
      text: "text-amber-400" 
    },
    info: { 
      icon: Info, 
      bg: "bg-blue-500/10", 
      border: "border-blue-500/50", 
      text: "text-blue-400" 
    },
    pending: { 
      icon: Clock, 
      bg: "bg-blue-500/10", 
      border: "border-blue-500/50", 
      text: "text-blue-400" 
    },
  };
  
  const config = statusConfig[status.status_type] || statusConfig.info;
  const StatusIcon = config.icon;
  
  let title = t("crewPortal.onboarding.incompleteTitle");
  let message = status.status_message || t("crewPortal.onboarding.incompleteDefaultBody");
  
  if (onboardingState.hasRejected) {
    title = t("crewPortal.onboarding.actionRequired.title");
    message = t("crewPortal.onboarding.actionRequired.body");
  } else if (onboardingState.hasPending) {
    title = t("crewPortal.onboarding.underReview.title");
    message = t("crewPortal.onboarding.underReview.body");
  } else if (onboardingState.needsDocuments) {
    title = t("crewPortal.onboarding.documentsRequired.title");
    message = t("crewPortal.onboarding.documentsRequired.body", {
      count: status.missing_required_documents?.length || 0,
    });
  }
  
  return (
    <Alert className={`mb-6 ${config.bg} ${config.border} ${config.text}`} data-testid="onboarding-status-banner">
      <StatusIcon className={`h-4 w-4 ${config.text}`} />
      <AlertTitle className="font-semibold">{title}</AlertTitle>
      <AlertDescription className="mt-1">{message}</AlertDescription>
    </Alert>
  );
}

/**
 * EVENTSFLO-CREW-PWA-ONBOARDING-REMINDER-AND-ADMIN-PENDING-DOC-ALERTS-V1
 *
 * Bilingual EN/FR onboarding reminder shown at the top of the home (Shifts)
 * view when the crew member's onboarding is incomplete (missing / pending /
 * rejected documents). Non-blocking — purely informational with a CTA that
 * jumps to the Documents view. Auto-hides when complete.
 */
function CrewOnboardingReminder({ status, onboardingState, onGoToDocuments }) {
  const { t } = useI18n();
  if (!status || onboardingState.isComplete) return null;

  const missing = status.missing_required_documents || [];
  const rejected = status.rejected_documents || [];
  const pending = status.document_summary?.pending || 0;

  let toneClass;
  let Icon;
  let title;
  let body;

  if (rejected.length > 0) {
    toneClass = "bg-red-500/10 border-red-500/40 text-red-600 dark:text-red-400";
    Icon = XCircle;
    title = t("crewPortal.onboarding.reminder.actionNeededTitle");
    body = t("crewPortal.onboarding.reminder.actionNeededBody", { count: rejected.length });
  } else if (missing.length > 0) {
    toneClass = "bg-amber-500/10 border-amber-500/40 text-amber-700 dark:text-amber-400";
    Icon = AlertTriangle;
    title = t("crewPortal.onboarding.reminder.finishTitle");
    body = t("crewPortal.onboarding.reminder.finishBody", { count: missing.length });
  } else if (pending > 0) {
    toneClass = "bg-blue-500/10 border-blue-500/40 text-blue-600 dark:text-blue-400";
    Icon = Clock;
    title = t("crewPortal.onboarding.reminder.underReviewTitle");
    body = t("crewPortal.onboarding.reminder.underReviewBody", { count: pending });
  } else {
    toneClass = "bg-amber-500/10 border-amber-500/40 text-amber-700 dark:text-amber-400";
    Icon = AlertTriangle;
    title = t("crewPortal.onboarding.reminder.fallbackTitle");
    body = t("crewPortal.onboarding.reminder.fallbackBody");
  }

  return (
    <Card
      className={`mb-4 border ${toneClass}`}
      data-testid="crew-onboarding-reminder"
    >
      <CardContent className="py-4">
        <div className="flex items-start gap-3">
          <Icon className="h-5 w-5 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0 space-y-2 text-foreground">
            <div>
              <div className="font-semibold text-sm">{title}</div>
              <div className="text-sm">{body}</div>
            </div>
            <Button
              size="sm"
              className="mt-2"
              onClick={onGoToDocuments}
              data-testid="crew-onboarding-reminder-cta"
            >
              <FileText className="h-3.5 w-3.5 mr-1.5" />
              {t("crewPortal.onboarding.reminder.uploadCta")}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * EVENTSFLO-CREW-PORTAL-WITHDRAW-AND-MESSAGES-V1
 * Crew in-app Messages view — renders the crew_messages feed for this staff member.
 */
function MessageRow({ msg, onTap }) {
  const { t } = useI18n();
  const msgFallback = t("crewPortal.messages_view.notificationFallback");
  const isUnread = !msg.read_at;
  const Icon = (() => {
    const t = (msg.type || "").toLowerCase();
    if (t === "shift_cancelled_by_admin") return Ban;
    if (t === "shift_withdrawn_by_crew") return LogOut;
    if (t.startsWith("shift_")) return Briefcase;
    return BellRing;
  })();
  return (
    <button
      type="button"
      onClick={() => onTap(msg)}
      className={`w-full text-left rounded-lg border p-3 transition-colors ${
        isUnread
          ? "bg-primary/5 border-primary/30 hover:bg-primary/10"
          : "bg-card hover:bg-muted/50"
      }`}
      data-testid={`crew-message-row-${msg.id}`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`mt-0.5 h-8 w-8 rounded-full flex items-center justify-center ${
            isUnread ? "bg-orange-500/10 text-orange-500" : "bg-muted text-muted-foreground"
          }`}
        >
          <Icon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p className={`text-sm ${isUnread ? "font-semibold" : "font-medium"} truncate`}>
              {msg.title || "Notification"}
            </p>
            {isUnread && (
              <span
                className="mt-1 h-2 w-2 rounded-full bg-orange-500 flex-shrink-0"
                aria-label="unread"
              />
            )}
          </div>
          {msg.body && (
            <p className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap line-clamp-3">
              {msg.body}
            </p>
          )}
          {msg.created_at && (
            <p className="text-[11px] text-muted-foreground mt-1">
              {new Date(msg.created_at).toLocaleString("en-GB")}
            </p>
          )}
        </div>
      </div>
    </button>
  );
}

function CrewMessagesView({ staffId, onUnreadChange, onNavigateView }) {
  const { t } = useI18n();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [markingAll, setMarkingAll] = useState(false);

  const load = useCallback(async () => {
    if (!staffId) return;
    setLoading(true);
    try {
      const res = await getCrewMessages(staffId, false, 100);
      const list = res?.data?.messages || res?.data || [];
      setMessages(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("Failed to load crew messages:", err);
      toast.error(t("crewPortal.messages_view.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [staffId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const unreadCount = useMemo(
    () => messages.filter((m) => !m.read_at).length,
    [messages],
  );

  useEffect(() => {
    if (onUnreadChange) onUnreadChange(unreadCount);
  }, [unreadCount, onUnreadChange]);

  const handleTap = async (msg) => {
    // Optimistically mark read
    if (!msg.read_at) {
      setMessages((prev) =>
        prev.map((m) => (m.id === msg.id ? { ...m, read_at: new Date().toISOString() } : m)),
      );
      try {
        await markCrewMessageRead(staffId, msg.id);
      } catch (err) {
        console.warn("Failed to mark message read:", err);
      }
    }
    // Navigate to link if present (shallow — keep inside PWA)
    const link = msg.link || "";
    if (link.startsWith("/crew-pwa/jobs") || link.startsWith("/crew/")) {
      onNavigateView && onNavigateView("shifts");
    } else if (link.startsWith("/crew-pwa/messages")) {
      /* already here */
    }
  };

  const handleMarkAll = async () => {
    if (!staffId || unreadCount === 0) return;
    setMarkingAll(true);
    try {
      await markAllCrewMessagesRead(staffId);
      const now = new Date().toISOString();
      setMessages((prev) => prev.map((m) => (m.read_at ? m : { ...m, read_at: now })));
      toast.success(t("crewPortal.messages_view.markAllReadToast"));
    } catch (err) {
      console.error("Mark-all-read failed:", err);
      toast.error(t("crewPortal.messages_view.markAllReadFailed"));
    } finally {
      setMarkingAll(false);
    }
  };

  if (!staffId) {
    return (
      <Card data-testid="messages-view-no-staff">
        <CardContent className="pt-6 text-center">
          <MessageSquare className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">{t("crewPortal.messages_view.signInPrompt")}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div data-testid="messages-view" className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-primary" />
          <h2 className="text-base font-semibold">{t("crewPortal.messages_view.title")}</h2>
          {unreadCount > 0 && (
            <Badge className="bg-orange-500 hover:bg-orange-500 text-white text-[11px]" data-testid="messages-unread-badge">
              {t("crewPortal.messages_view.unreadBadge", { count: unreadCount })}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={load}
            disabled={loading}
            data-testid="messages-refresh-btn"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleMarkAll}
            disabled={markingAll || unreadCount === 0}
            data-testid="messages-mark-all-read-btn"
          >
            {markingAll ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <CheckCheck className="h-4 w-4 mr-1" />
            )}
            {t("crewPortal.messages_view.markAllRead")}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12" data-testid="messages-loading">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : messages.length === 0 ? (
        <Card data-testid="messages-empty-state">
          <CardContent className="pt-6 text-center">
            <MessageSquare className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
            <p className="text-sm font-medium">{t("crewPortal.messages_view.emptyTitle")}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {t("crewPortal.messages_view.emptyHint")}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="messages-list">
          {messages.map((msg) => (
            <MessageRow key={msg.id} msg={msg} onTap={handleTap} />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Main Crew Portal Component
 */
export default function CrewPortalPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // EVENTSFLO-PORTAL-I18N-PHASE-2C-CREW-PORTAL-COVERAGE-V1
  const { t } = useI18n();
  
  // EVENTSFLO-CREW-PORTAL-SHARE-LINK-STAFF-FILTER-V1: deep-link share param.
  // Planner generates `/crew?staff=<staffId>` links. CrewPortal is owner-
  // scoped (always shows the logged-in user's own data), so the param is
  // used as a hint: when present, default the landing view to "shifts"
  // (the jobs list) regardless of any stale `view` param, and silently
  // warn in dev if the param doesn't match the logged-in staff id.
  const staffIdFromUrl = searchParams.get("staff");

  // Get initial view from URL or default to shifts. If a share link with
  // ?staff=… was used, force the jobs/shifts view as the entry point.
  const initialView = staffIdFromUrl ? "shifts" : (searchParams.get("view") || "shifts");
  
  const [user, setUser] = useState(null);
  const [staff, setStaff] = useState(null);
  // EVENTSFLO-CREW-PWA-ACTIVE-ROUTE-AND-UI-SPLIT-AUDIT-FIX-V1
  const [profileRequestOpen, setProfileRequestOpen] = useState(false);
  const [profileRequestMessage, setProfileRequestMessage] = useState("");
  const [profileRequestSending, setProfileRequestSending] = useState(false);

  const handleSubmitProfileUpdateRequest = async () => {
    const staffId = staff?.id;
    if (!staffId) {
      toast.error(t("crewPortal.profileView.requestDialog.noStaffId"));
      return;
    }
    setProfileRequestSending(true);
    try {
      await api.post("/crew/me/profile-update-request", {
        staff_id: staffId,
        message: profileRequestMessage.trim() || null,
      });
      toast.success(t("crewPortal.profileView.requestDialog.sendSuccess"));
      setProfileRequestOpen(false);
    } catch {
      toast.error(t("crewPortal.profileView.requestDialog.sendFailed"));
    } finally {
      setProfileRequestSending(false);
    }
  };
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeView, setActiveView] = useState(initialView);
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  // EVENTSFLO-CREW-PORTAL-WITHDRAW-AND-MESSAGES-V1: unread message count for nav badge
  const [unreadMessages, setUnreadMessages] = useState(0);
  
  // Profile form
  const [profileForm, setProfileForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
  });
  
  // EF-CREW-PWA-COMPLETE-PROFILE-TABS-AND-THEME-FIX-V1: Extended profile data
  const [extendedProfile, setExtendedProfile] = useState({
    // Employment
    job_title: "",
    employment_type: "",
    start_date: "",
    hourly_rate: "",
    payment_method: "",
    bank_details: "",
    
    // Suitability
    has_driving_license: false,
    driving_license_type: "",
    has_own_vehicle: false,
    languages: [],
    skills: [],
    certifications: [],
    availability_notes: "",
    
    // Address
    address_line_1: "",
    address_line_2: "",
    city: "",
    postcode: "",
    country: "",
    
    // Emergency
    emergency_contact_name: "",
    emergency_contact_phone: "",
    
    // Additional
    nationality: "",
    date_of_birth: "",
    gender: "",
    notes: "",
  });
  
  // Profile section expansion state
  const [expandedSections, setExpandedSections] = useState({
    personal: true,
    employment: false,
    suitability: false,
    compliance: false,
    // EVENTSFLO-CREW-INBOX-ONBOARDING-HEALTH-RECORDS-AND-DISPLAY-NAME-V1
    health: false,
  });
  // EVENTSFLO-CREW-INBOX-ONBOARDING-HEALTH-RECORDS-AND-DISPLAY-NAME-V1
  // Health records (France Carte Vitale, Monaco CCSS, etc.) for the
  // active crew. Read-only on this view.
  const [healthRecords, setHealthRecords] = useState([]);
  
  // EF-CREW-PWA-NAVIGATION-AND-UTILITY-PAGES-V1: Theme state with localStorage persistence
  // EF-FIX-THEME-PERSISTENCE-ON-INITIAL-LOAD-V1: Read theme from DOM (set by index.html) or localStorage
  const [theme, setTheme] = useState(() => {
    // First check what's already applied by index.html script
    const appliedTheme = document.documentElement.getAttribute("data-theme");
    if (appliedTheme) {
      return appliedTheme;
    }
    // Fallback to localStorage
    const saved = localStorage.getItem("icrew_theme");
    return saved || (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light");
  });
  
  // Track if this is the initial mount - don't reapply theme on mount
  const isInitialMount = useRef(true);
  
  // EF-FIX-THEME-PERSISTENCE-ON-INITIAL-LOAD-V1: Only apply theme when user toggles, not on mount
  useEffect(() => {
    // Skip the initial mount - index.html already applied the theme
    if (isInitialMount.current) {
      isInitialMount.current = false;
      // Just ensure localStorage is in sync with current state
      localStorage.setItem("icrew_theme", theme);
      return;
    }
    
    // User toggled the theme - apply it
    const root = document.documentElement;
    root.setAttribute("data-theme", theme);
    
    // Toggle classes
    if (theme === "dark") {
      root.classList.remove("light");
      root.classList.add("dark");
      
      // Directly set CSS variables for dark mode
      root.style.setProperty("--background", "240 10% 3.9%");
      root.style.setProperty("--foreground", "0 0% 98%");
      root.style.setProperty("--card", "240 10% 9%");
      root.style.setProperty("--card-foreground", "0 0% 98%");
      root.style.setProperty("--popover", "240 10% 3.9%");
      root.style.setProperty("--popover-foreground", "0 0% 98%");
      root.style.setProperty("--primary", "187 94% 43%");
      root.style.setProperty("--secondary", "240 5% 17%");
      root.style.setProperty("--secondary-foreground", "0 0% 98%");
      root.style.setProperty("--muted", "240 5% 17%");
      root.style.setProperty("--muted-foreground", "240 5% 65%");
      root.style.setProperty("--accent", "240 5% 17%");
      root.style.setProperty("--accent-foreground", "0 0% 98%");
      root.style.setProperty("--border", "240 5% 17%");
      root.style.setProperty("--input", "240 5% 17%");
      root.style.setProperty("--ring", "187 94% 43%");
    } else {
      root.classList.remove("dark");
      root.classList.add("light");
      
      // Directly set CSS variables for light mode
      root.style.setProperty("--background", "210 20% 98%");
      root.style.setProperty("--foreground", "222 47% 11%");
      root.style.setProperty("--card", "0 0% 100%");
      root.style.setProperty("--card-foreground", "222 47% 11%");
      root.style.setProperty("--popover", "0 0% 100%");
      root.style.setProperty("--popover-foreground", "222 47% 11%");
      root.style.setProperty("--primary", "217 91% 60%");
      root.style.setProperty("--secondary", "220 14% 96%");
      root.style.setProperty("--secondary-foreground", "222 47% 11%");
      root.style.setProperty("--muted", "220 14% 96%");
      root.style.setProperty("--muted-foreground", "220 13% 40%");
      root.style.setProperty("--accent", "220 14% 96%");
      root.style.setProperty("--accent-foreground", "222 47% 11%");
      root.style.setProperty("--border", "220 13% 85%");
      root.style.setProperty("--input", "220 13% 85%");
      root.style.setProperty("--ring", "217 91% 60%");
    }
    
    localStorage.setItem("icrew_theme", theme);
  }, [theme]);
  
  const handleThemeToggle = useCallback(() => {
    setTheme(prev => prev === "dark" ? "light" : "dark");
  }, []);
  
  // Calculate onboarding state
  const onboardingState = useMemo(() => getOnboardingState(onboardingStatus), [onboardingStatus]);
  
  // Load crew data
  useEffect(() => {
    async function loadCrewData() {
      try {
        const token = localStorage.getItem("eventsflo_auth_token");
        const userData = localStorage.getItem("eventsflo_auth_user") || localStorage.getItem("eventsflo_user");
        
        if (!token || !userData) {
          toast.error(t("crewPortal.loginRequired"));
          navigate("/login");
          return;
        }
        
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        // EVENTSFLO-CREW-PORTAL-WITHDRAW-AND-MESSAGES-V1: seed staff state from
        // login payload's staff_id so CrewMessagesView and unread polling have
        // a staffId immediately, even when /crew/onboarding/profile returns 403
        // (staff users are not crew_candidates).
        if (parsedUser?.staff_id) {
          setStaff({ id: parsedUser.staff_id });
        }
        
        // Fetch profile
        try {
          const profileRes = await fetch(`${API_URL}${CREW_ENDPOINTS.getProfile}`, {
            headers: { "Authorization": `Bearer ${token}` }
          });
          
          if (profileRes.ok) {
            const profileData = await profileRes.json();
            setProfileForm({
              first_name: profileData.first_name || "",
              last_name: profileData.last_name || "",
              email: profileData.email || parsedUser.email || "",
              phone: profileData.phone || "",
            });
            
            // EF-CREW-PWA-COMPLETE-PROFILE-TABS-AND-THEME-FIX-V1: Load extended profile data
            setExtendedProfile(prev => ({
              ...prev,
              // Employment
              job_title: profileData.job_title || "",
              employment_type: profileData.employment_type || "",
              start_date: profileData.start_date || "",
              hourly_rate: profileData.hourly_rate || "",
              payment_method: profileData.payment_method || "",
              bank_details: profileData.bank_details || "",
              
              // Suitability
              has_driving_license: profileData.has_driving_license || false,
              driving_license_type: profileData.driving_license_type || "",
              has_own_vehicle: profileData.has_own_vehicle || false,
              languages: profileData.languages || [],
              skills: profileData.skills || [],
              certifications: profileData.certifications || [],
              availability_notes: profileData.availability_notes || "",
              
              // Address
              address_line_1: profileData.address_line_1 || "",
              address_line_2: profileData.address_line_2 || "",
              city: profileData.city || "",
              postcode: profileData.postcode || "",
              country: profileData.country || "",
              
              // Emergency
              emergency_contact_name: profileData.emergency_contact_name || "",
              emergency_contact_phone: profileData.emergency_contact_phone || "",
              
              // Additional
              nationality: profileData.nationality || "",
              date_of_birth: profileData.date_of_birth || "",
              gender: profileData.gender || "",
              notes: profileData.notes || "",
            }));
            
            if (profileData.staff_id) {
              setStaff({ id: profileData.staff_id });
            }
          } else {
            setProfileForm({
              first_name: parsedUser.first_name || "",
              last_name: parsedUser.last_name || "",
              email: parsedUser.email || "",
              phone: parsedUser.phone || parsedUser.mobile || "",
            });
          }
        } catch (profileErr) {
          console.warn("Failed to load profile:", profileErr);
          setProfileForm({
            first_name: parsedUser.first_name || "",
            last_name: parsedUser.last_name || "",
            email: parsedUser.email || "",
            phone: parsedUser.phone || parsedUser.mobile || "",
          });
        }
        
        // Fetch company info
        try {
          const companyRes = await fetch(`${API_URL}${CREW_ENDPOINTS.companyInfo}`, {
            headers: { "Authorization": `Bearer ${token}` }
          });
          if (companyRes.ok) {
            const companyData = await companyRes.json();
            setCompanyInfo(companyData);
          }
        } catch (companyErr) {
          console.warn("Failed to load company info:", companyErr);
        }
        
        // Fetch onboarding status.
        // - Crew candidates use the existing `/api/crew/onboarding/status` (works
        //   off `crew_candidates` and rejects `user_type=crew` with 403).
        // - Already-onboarded crew (JWT carries `staff_id`) use the new
        //   `/api/crew/me/onboarding-status` introduced in V2.
        // EVENTSFLO-CREW-PWA-ONBOARDING-REMINDER-AND-ADMIN-PENDING-DOC-ALERTS-V1
        try {
          const isOnboardedCrew = !!parsedUser?.staff_id;
          const url = isOnboardedCrew
            ? `${API_URL}/api/crew/me/onboarding-status`
            : `${API_URL}${CREW_ENDPOINTS.onboardingStatus}`;
          const statusRes = await fetch(url, {
            headers: { "Authorization": `Bearer ${token}` }
          });
          if (statusRes.ok) {
            const statusData = await statusRes.json();
            setOnboardingStatus(statusData);
          }
        } catch (statusErr) {
          console.warn("Failed to load onboarding status:", statusErr);
        }
      } catch (err) {
        console.error("Error loading crew data:", err);
        toast.error(t("crewPortal.loadProfileFailed"));
      } finally {
        setLoading(false);
      }
    }
    
    loadCrewData();
  }, [navigate]);

  // EVENTSFLO-CREW-PORTAL-I18N-PERSIST-PREFERENCE-V1
  // Hydrate i18next from the server-side preferred portal language
  // (per-crew, persisted on the staff row) and write back every change.
  // Falls back gracefully to localStorage when:
  //   * staff_id is not yet known (e.g. crew candidate path)
  //   * the GET returns 403/404/network error
  //   * the server stores `null`
  useEffect(() => {
    const staffId = staff?.id;
    if (!staffId) return undefined;

    const token = localStorage.getItem("eventsflo_auth_token");
    if (!token) return undefined;

    let cancelled = false;
    // EVENTSFLO-CREW-PORTAL-I18N-PERSIST-PREFERENCE-V1 — track the
    // most recently server-synced language so the hydration-driven
    // `i18n.changeLanguage(...)` does not echo an idempotent PUT.
    let lastSyncedLang = null;

    // 1) Hydrate once on first staff-id resolution.
    (async () => {
      try {
        const res = await fetch(
          `${API_URL}${CREW_ENDPOINTS.getPreferredLanguage}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const remote = data?.preferred_portal_language;
        if (remote && ["en", "fr", "ru"].includes(remote)) {
          lastSyncedLang = remote;
          if (i18n.language !== remote) i18n.changeLanguage(remote);
        }
      } catch (err) {
        // Non-fatal — keep localStorage / default.
        console.warn("Failed to hydrate preferred language:", err?.message || err);
      }
    })();

    // 2) Persist every subsequent change. The I18nProvider already
    // mirrors into localStorage + <html lang>; here we add server sync.
    const onChange = async (lng) => {
      if (!["en", "fr", "ru"].includes(lng)) return;
      if (lng === lastSyncedLang) return; // skip idempotent self-PUT
      try {
        await fetch(`${API_URL}${CREW_ENDPOINTS.setPreferredLanguage}`, {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ preferred_portal_language: lng }),
        });
        lastSyncedLang = lng;
      } catch (err) {
        console.warn("Failed to persist preferred language:", err?.message || err);
      }
    };

    i18n.on("languageChanged", onChange);
    return () => {
      cancelled = true;
      i18n.off("languageChanged", onChange);
    };
  }, [staff?.id]);

  // EVENTSFLO-CREW-PORTAL-WITHDRAW-AND-MESSAGES-V1: poll unread message count every 30s
  useEffect(() => {
    const staffId = staff?.id;
    if (!staffId) return;
    let cancelled = false;

    const fetchCount = async () => {
      try {
        const res = await getCrewMessagesUnreadCount(staffId);
        const count = Number(res?.data?.unread_count ?? res?.data?.count ?? 0) || 0;
        if (!cancelled) setUnreadMessages(count);
      } catch (err) {
        // Silent fail — badge just won't update
        console.warn("Failed to fetch unread count:", err?.message || err);
      }
    };

    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [staff?.id]);

  // EVENTSFLO-CREW-INBOX-ONBOARDING-HEALTH-RECORDS-AND-DISPLAY-NAME-V1
  // Load this crew member's health records (France/Monaco). The endpoint
  // is JWT-self (`/crew/me/health-records`) so no admin token is needed.
  useEffect(() => {
    let cancelled = false;
    const loadHealth = async () => {
      try {
        const res = await api.get("/crew/me/health-records");
        const rows = res?.data?.records || [];
        if (!cancelled) setHealthRecords(Array.isArray(rows) ? rows : []);
      } catch (err) {
        // Silent — section auto-hides when there are no records.
        console.warn("Failed to load health records:", err?.message || err);
      }
    };
    if (staff?.id) loadHealth();
    return () => { cancelled = true; };
  }, [staff?.id]);

  // EVENTSFLO-CREW-PORTAL-SHARE-LINK-STAFF-FILTER-V1: silently validate the
  // ?staff=… deep-link hint against the logged-in staff id. CrewPortal is
  // owner-scoped, so a mismatched param is just ignored (no UI change, no
  // toast) — we only warn in dev to help diagnose stale share links.
  useEffect(() => {
    if (!staffIdFromUrl || !staff?.id) return;
    if (staffIdFromUrl !== staff.id && process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn(
        `[CrewPortal] share-link ?staff=${staffIdFromUrl} does not match the logged-in staff id ${staff.id} — ignoring.`,
      );
    }
  }, [staffIdFromUrl, staff?.id]);
  
  // Handle navigation
  const handleNavigate = useCallback((view) => {
    setActiveView(view);
    setSearchParams({ view });
    setMenuOpen(false);
  }, [setSearchParams]);
  
  // Handle logout
  const handleLogout = useCallback(() => {
    localStorage.removeItem("eventsflo_auth_token");
    localStorage.removeItem("eventsflo_auth_user");
    localStorage.removeItem("eventsflo_user");
    toast.success(t("crewPortal.loggedOut"));
    navigate("/login");
  }, [navigate, t]);
  
  // EF-CREW-PWA-COMPLETE-PROFILE-TABS-AND-THEME-FIX-V1: Toggle section expansion
  const toggleSection = useCallback((section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  }, []);
  
  // Handle profile save
  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("eventsflo_auth_token");
      
      const response = await fetch(`${API_URL}${CREW_ENDPOINTS.updateProfile}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify(profileForm),
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || t("crewPortal.profileView.personal.saveFailed"));
      }
      
      toast.success(t("crewPortal.profileView.personal.saveSuccess"));
    } catch (err) {
      console.error("Save profile error:", err);
      toast.error(err.message || t("crewPortal.profileView.personal.saveFailed"));
    } finally {
      setSaving(false);
    }
  };
  
  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
          <p className="mt-2 text-muted-foreground">{t("crewPortal.header.loadingPortal")}</p>
        </div>
      </div>
    );
  }
  
  // Get view title
  const getViewTitle = () => {
    switch (activeView) {
      case "shifts": return t("crewPortal.header.viewTitle.shifts");
      case "calendar": return t("crewPortal.header.viewTitle.calendar");
      case "messages": return t("crewPortal.header.viewTitle.messages");
      case "profile": return t("crewPortal.header.viewTitle.profile");
      case "documents": return t("crewPortal.header.viewTitle.documents");
      case "install": return t("crewPortal.header.viewTitle.install");
      case "about": return t("crewPortal.header.viewTitle.about");
      default: return t("crewPortal.header.viewTitle.default");
    }
  };
  
  return (
    <div className="min-h-screen bg-background" data-testid="crew-portal-page">
      {/* Navigation Menu - EF-PWA-BRANDING-V1-STEP4: Pass companyInfo */}
      <NavigationMenu
        isOpen={menuOpen}
        onClose={() => setMenuOpen(false)}
        activeView={activeView}
        onNavigate={handleNavigate}
        onboardingComplete={onboardingState.isComplete}
        onLogout={handleLogout}
        companyInfo={companyInfo}
        unreadMessages={unreadMessages}
      />
      
      {/* Header */}
      <header className="sticky top-0 z-30 border-b bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/60">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => setMenuOpen(true)}
              data-testid="hamburger-menu-btn"
            >
              <Menu className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-lg font-semibold">{getViewTitle()}</h1>
              <p className="text-xs text-muted-foreground">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* EVENTSFLO-CREW-PWA-ONBOARDING-REMINDER-AND-ADMIN-PENDING-DOC-ALERTS-V1
                Persistent top-bar warning icon while onboarding is incomplete.
                Tapping jumps straight to the Documents view. Auto-hides when
                onboarding is complete — never blocks the app. */}
            {!onboardingState.isComplete && (
              <Button
                variant="ghost"
                size="icon"
                className="text-amber-500 hover:text-amber-600 relative"
                onClick={() => handleNavigate("documents")}
                aria-label={t("crewPortal.header.onboardingIncompleteAria")}
                title={t("crewPortal.header.onboardingIncompleteTitle")}
                data-testid="header-onboarding-warning"
              >
                <AlertTriangle className="h-5 w-5" />
                <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
              </Button>
            )}
            {/* EVENTSFLO-CREW-PORTAL-I18N-PERSIST-PREFERENCE-V1
                Header-level compact language selector — makes the
                language choice discoverable without navigating into
                Settings. The Settings inline variant remains for the
                full pick-from-list experience. */}
            <LanguageSelector
              variant="compact"
              testId="crew-header-language-selector"
            />
            {/* Quick theme toggle in header */}
            <Button 
              variant="ghost" 
              size="icon"
              onClick={handleThemeToggle}
              data-testid="quick-theme-toggle"
            >
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6 max-w-4xl pb-20">
        {/* EVENTSFLO-CREW-PWA-ONBOARDING-REMINDER-AND-ADMIN-PENDING-DOC-ALERTS-V1
            Bilingual EN/FR onboarding reminder on the home (Shifts) view —
            non-blocking, hides itself when onboarding is complete. */}
        {activeView === "shifts" && (
          <CrewOnboardingReminder
            status={onboardingStatus}
            onboardingState={onboardingState}
            onGoToDocuments={() => handleNavigate("documents")}
          />
        )}

        {/* Show onboarding banner on primary views */}
        {(activeView === "shifts" || activeView === "calendar" || activeView === "profile" || activeView === "documents") && (
          <OnboardingStatusBanner status={onboardingStatus} onboardingState={onboardingState} />
        )}
        
        {/* Shifts View */}
        {activeView === "shifts" && (
          <div data-testid="shifts-view">
            {onboardingState.isComplete ? (
              <CrewShiftList />
            ) : (
              <Card>
                <CardContent className="pt-6 text-center">
                  <Lock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="font-semibold text-lg">{t("crewPortal.shiftsLocked.title")}</h3>
                  <p className="text-muted-foreground mt-2">
                    {t("crewPortal.shiftsLocked.description")}
                  </p>
                  <Button 
                    className="mt-4" 
                    onClick={() => handleNavigate("documents")}
                  >
                    {t("crewPortal.shiftsLocked.cta")}
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        )}
        
        {/* Calendar View - EF-CREW-CALENDAR-VIEW-V1 */}
        {activeView === "calendar" && (
          <div data-testid="calendar-view">
            {onboardingState.isComplete ? (
              <CrewCalendarView />
            ) : (
              <Card>
                <CardContent className="pt-6 text-center">
                  <Lock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="font-semibold text-lg">{t("crewPortal.calendarLocked.title")}</h3>
                  <p className="text-muted-foreground mt-2">
                    {t("crewPortal.calendarLocked.description")}
                  </p>
                  <Button 
                    className="mt-4" 
                    onClick={() => handleNavigate("documents")}
                  >
                    {t("crewPortal.calendarLocked.cta")}
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* EVENTSFLO-CREW-PORTAL-WITHDRAW-AND-MESSAGES-V1: Messages view */}
        {activeView === "messages" && (
          <CrewMessagesView
            staffId={staff?.id}
            onUnreadChange={setUnreadMessages}
            onNavigateView={handleNavigate}
          />
        )}
        
        {/* Profile View - EF-CREW-PWA-COMPLETE-PROFILE-TABS-AND-THEME-FIX-V1 */}
        {activeView === "profile" && (
          <div data-testid="profile-view" className="space-y-4">
            {/* Profile Header Card */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="h-8 w-8 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold">
                      {profileForm.first_name || t("crewPortal.profileView.crewMemberFallback")} {profileForm.last_name}
                    </h2>
                    <p className="text-muted-foreground">
                      {companyInfo?.company_name || "EventsFlo"}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Personal Information Section */}
            <Card>
              <button
                onClick={() => toggleSection("personal")}
                className="w-full"
                data-testid="section-personal-toggle"
              >
                <CardHeader className="flex flex-row items-center justify-between py-4">
                  <div className="flex items-center gap-2">
                    <User className="h-5 w-5 text-primary" />
                    <CardTitle className="text-base">{t("crewPortal.profileView.personal.title")}</CardTitle>
                  </div>
                  {expandedSections.personal ? (
                    <ChevronUp className="h-5 w-5 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-muted-foreground" />
                  )}
                </CardHeader>
              </button>
              {expandedSections.personal && (
                <CardContent className="pt-0 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="first_name">{t("crewPortal.profileView.personal.firstName")}</Label>
                      <Input
                        id="first_name"
                        value={profileForm.first_name}
                        onChange={(e) => setProfileForm(p => ({ ...p, first_name: e.target.value }))}
                        data-testid="profile-first-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="last_name">{t("crewPortal.profileView.personal.lastName")}</Label>
                      <Input
                        id="last_name"
                        value={profileForm.last_name}
                        onChange={(e) => setProfileForm(p => ({ ...p, last_name: e.target.value }))}
                        data-testid="profile-last-name"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="email">{t("crewPortal.profileView.personal.email")}</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="email"
                        type="email"
                        value={profileForm.email}
                        onChange={(e) => setProfileForm(p => ({ ...p, email: e.target.value }))}
                        className="pl-10"
                        data-testid="profile-email"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="phone">{t("crewPortal.profileView.personal.mobilePhone")}</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="phone"
                        value={profileForm.phone}
                        onChange={(e) => setProfileForm(p => ({ ...p, phone: e.target.value }))}
                        className="pl-10"
                        placeholder={t("crewPortal.profileView.personal.phonePlaceholder")}
                        data-testid="profile-phone"
                      />
                    </div>
                  </div>
                  
                  {/* Address Fields */}
                  <div className="pt-2 border-t">
                    <Label className="text-sm text-muted-foreground mb-2 block">{t("crewPortal.profileView.personal.address")}</Label>
                    <div className="space-y-3">
                      <ProfileField 
                        icon={MapPin} 
                        label={t("crewPortal.profileView.personal.streetAddress")} 
                        value={extendedProfile.address_line_1} 
                      />
                      {extendedProfile.address_line_2 && (
                        <ProfileField 
                          label={t("crewPortal.profileView.personal.addressLine2")} 
                          value={extendedProfile.address_line_2} 
                        />
                      )}
                      <div className="grid grid-cols-2 gap-2">
                        <ProfileField label={t("crewPortal.profileView.personal.city")} value={extendedProfile.city} />
                        <ProfileField label={t("crewPortal.profileView.personal.postcode")} value={extendedProfile.postcode} />
                      </div>
                      <ProfileField 
                        icon={Flag} 
                        label={t("crewPortal.profileView.personal.country")} 
                        value={extendedProfile.country} 
                      />
                    </div>
                  </div>
                  
                  {/* Emergency Contact */}
                  {(extendedProfile.emergency_contact_name || extendedProfile.emergency_contact_phone) && (
                    <div className="pt-2 border-t">
                      <Label className="text-sm text-muted-foreground mb-2 block">{t("crewPortal.profileView.personal.emergencyContact")}</Label>
                      <div className="space-y-2">
                        <ProfileField 
                          icon={Heart} 
                          label={t("crewPortal.profileView.personal.emergencyName")} 
                          value={extendedProfile.emergency_contact_name} 
                        />
                        <ProfileField 
                          icon={Phone} 
                          label={t("crewPortal.profileView.personal.emergencyPhone")} 
                          value={extendedProfile.emergency_contact_phone} 
                        />
                      </div>
                    </div>
                  )}
                  
                  <Button 
                    onClick={handleSaveProfile} 
                    disabled={saving}
                    className="w-full"
                    data-testid="save-profile-btn"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {t("crewPortal.profileView.personal.saving")}
                      </>
                    ) : (
                      t("crewPortal.profileView.personal.saveProfile")
                    )}
                  </Button>

                  {/* EVENTSFLO-CREW-PWA-ACTIVE-ROUTE-AND-UI-SPLIT-AUDIT-FIX-V1
                      Request Profile Update — routes to admin Message Centre. */}
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full mt-2"
                    onClick={() => {
                      setProfileRequestMessage("");
                      setProfileRequestOpen(true);
                    }}
                    data-testid="portal-request-profile-update-btn"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    {t("crewPortal.profileView.personal.requestProfileUpdate")}
                  </Button>
                </CardContent>
              )}
            </Card>
            
            {/* Employment Section */}
            <Card>
              <button
                onClick={() => toggleSection("employment")}
                className="w-full"
                data-testid="section-employment-toggle"
              >
                <CardHeader className="flex flex-row items-center justify-between py-4">
                  <div className="flex items-center gap-2">
                    <Briefcase className="h-5 w-5 text-primary" />
                    <CardTitle className="text-base">{t("crewPortal.profileView.employment.title")}</CardTitle>
                  </div>
                  {expandedSections.employment ? (
                    <ChevronUp className="h-5 w-5 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-muted-foreground" />
                  )}
                </CardHeader>
              </button>
              {expandedSections.employment && (
                <CardContent className="pt-0 space-y-3">
                  <ProfileField 
                    icon={Briefcase} 
                    label={t("crewPortal.profileView.employment.jobTitle")} 
                    value={extendedProfile.job_title || t("crewPortal.profileView.employment.notSpecified")} 
                  />
                  <ProfileField 
                    label={t("crewPortal.profileView.employment.employmentType")} 
                    value={extendedProfile.employment_type || t("crewPortal.profileView.employment.notSpecified")} 
                  />
                  <ProfileField 
                    icon={Calendar} 
                    label={t("crewPortal.profileView.employment.startDate")} 
                    value={extendedProfile.start_date ? new Date(extendedProfile.start_date).toLocaleDateString("en-GB") : t("crewPortal.profileView.employment.notSpecified")} 
                  />
                  <div className="pt-2 border-t">
                    <Label className="text-sm text-muted-foreground mb-2 block">{t("crewPortal.profileView.employment.payment")}</Label>
                    <ProfileField 
                      icon={CreditCard} 
                      label={t("crewPortal.profileView.employment.paymentMethod")} 
                      value={extendedProfile.payment_method || t("crewPortal.profileView.employment.notSpecified")} 
                    />
                  </div>
                  <p className="text-xs text-muted-foreground pt-2">
                    {t("crewPortal.profileView.employment.contactAdminEmployment")}
                  </p>
                </CardContent>
              )}
            </Card>
            
            {/* Suitability Section */}
            <Card>
              <button
                onClick={() => toggleSection("suitability")}
                className="w-full"
                data-testid="section-suitability-toggle"
              >
                <CardHeader className="flex flex-row items-center justify-between py-4">
                  <div className="flex items-center gap-2">
                    <Shield className="h-5 w-5 text-primary" />
                    <CardTitle className="text-base">{t("crewPortal.profileView.suitability.title")}</CardTitle>
                  </div>
                  {expandedSections.suitability ? (
                    <ChevronUp className="h-5 w-5 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-muted-foreground" />
                  )}
                </CardHeader>
              </button>
              {expandedSections.suitability && (
                <CardContent className="pt-0 space-y-3">
                  {/* Driving */}
                  <div className="flex items-center justify-between py-2">
                    <div className="flex items-center gap-2">
                      <Car className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">{t("crewPortal.profileView.suitability.drivingLicense")}</span>
                    </div>
                    <Badge variant={extendedProfile.has_driving_license ? "default" : "secondary"}>
                      {extendedProfile.has_driving_license ? t("crewPortal.profileView.suitability.yes") : t("crewPortal.profileView.suitability.no")}
                    </Badge>
                  </div>
                  {extendedProfile.driving_license_type && (
                    <ProfileField 
                      label={t("crewPortal.profileView.suitability.licenseType")} 
                      value={extendedProfile.driving_license_type} 
                    />
                  )}
                  <div className="flex items-center justify-between py-2">
                    <div className="flex items-center gap-2">
                      <Car className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">{t("crewPortal.profileView.suitability.ownVehicle")}</span>
                    </div>
                    <Badge variant={extendedProfile.has_own_vehicle ? "default" : "secondary"}>
                      {extendedProfile.has_own_vehicle ? t("crewPortal.profileView.suitability.yes") : t("crewPortal.profileView.suitability.no")}
                    </Badge>
                  </div>
                  
                  {/* Languages */}
                  {extendedProfile.languages?.length > 0 && (
                    <div className="pt-2 border-t">
                      <Label className="text-sm text-muted-foreground mb-2 block">{t("crewPortal.profileView.suitability.languages")}</Label>
                      <div className="flex flex-wrap gap-2">
                        {extendedProfile.languages.map((lang, idx) => (
                          <Badge key={idx} variant="outline">{lang}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Skills */}
                  {extendedProfile.skills?.length > 0 && (
                    <div className="pt-2 border-t">
                      <Label className="text-sm text-muted-foreground mb-2 block">{t("crewPortal.profileView.suitability.skills")}</Label>
                      <div className="flex flex-wrap gap-2">
                        {extendedProfile.skills.map((skill, idx) => (
                          <Badge key={idx} variant="outline">{skill}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Certifications */}
                  {extendedProfile.certifications?.length > 0 && (
                    <div className="pt-2 border-t">
                      <Label className="text-sm text-muted-foreground mb-2 block">{t("crewPortal.profileView.suitability.certifications")}</Label>
                      <div className="flex flex-wrap gap-2">
                        {extendedProfile.certifications.map((cert, idx) => (
                          <Badge key={idx} variant="secondary">{cert}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {extendedProfile.availability_notes && (
                    <div className="pt-2 border-t">
                      <Label className="text-sm text-muted-foreground mb-2 block">{t("crewPortal.profileView.suitability.availabilityNotes")}</Label>
                      <p className="text-sm">{extendedProfile.availability_notes}</p>
                    </div>
                  )}
                  
                  <p className="text-xs text-muted-foreground pt-2">
                    {t("crewPortal.profileView.suitability.contactAdminSuitability")}
                  </p>
                </CardContent>
              )}
            </Card>
            
            {/* Compliance Section */}
            <Card>
              <button
                onClick={() => toggleSection("compliance")}
                className="w-full"
                data-testid="section-compliance-toggle"
              >
                <CardHeader className="flex flex-row items-center justify-between py-4">
                  <div className="flex items-center gap-2">
                    <FileCheck className="h-5 w-5 text-primary" />
                    <CardTitle className="text-base">{t("crewPortal.profileView.compliance.title")}</CardTitle>
                  </div>
                  {expandedSections.compliance ? (
                    <ChevronUp className="h-5 w-5 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-muted-foreground" />
                  )}
                </CardHeader>
              </button>
              {expandedSections.compliance && (
                <CardContent className="pt-0 space-y-3">
                  {/* Document Status Summary */}
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                      <div className="text-2xl font-bold text-emerald-500">
                        {onboardingStatus?.document_summary?.approved || 0}
                      </div>
                      <div className="text-xs text-muted-foreground">{t("crewPortal.profileView.compliance.approved")}</div>
                    </div>
                    <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                      <div className="text-2xl font-bold text-amber-500">
                        {onboardingStatus?.document_summary?.pending || 0}
                      </div>
                      <div className="text-xs text-muted-foreground">{t("crewPortal.profileView.compliance.pending")}</div>
                    </div>
                    <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                      <div className="text-2xl font-bold text-red-500">
                        {onboardingStatus?.rejected_documents?.length || 0}
                      </div>
                      <div className="text-xs text-muted-foreground">{t("crewPortal.profileView.compliance.rejected")}</div>
                    </div>
                  </div>
                  
                  {/* Missing Documents Alert */}
                  {onboardingStatus?.missing_required_documents?.length > 0 && (
                    <Alert className="bg-amber-500/10 border-amber-500/30">
                      <AlertTriangle className="h-4 w-4 text-amber-500" />
                      <AlertTitle className="text-amber-600">{t("crewPortal.profileView.compliance.missingTitle")}</AlertTitle>
                      <AlertDescription className="text-sm">
                        {t("crewPortal.profileView.compliance.missingBody", { count: onboardingStatus.missing_required_documents.length })}
                      </AlertDescription>
                    </Alert>
                  )}
                  
                  {/* Link to Documents */}
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => handleNavigate("documents")}
                    data-testid="go-to-documents-btn"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    {t("crewPortal.profileView.compliance.viewUploadCta")}
                  </Button>
                  
                  {/* Compliance Status */}
                  <div className="pt-2 border-t">
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm">{t("crewPortal.profileView.compliance.overallStatus")}</span>
                      {onboardingState.isComplete ? (
                        <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/30">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          {t("crewPortal.profileView.compliance.compliant")}
                        </Badge>
                      ) : (
                        <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/30">
                          <Clock className="h-3 w-3 mr-1" />
                          {t("crewPortal.profileView.compliance.inProgress")}
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>

            {/* EVENTSFLO-CREW-INBOX-ONBOARDING-HEALTH-RECORDS-AND-DISPLAY-NAME-V1
                Health section — read-only. Auto-hides when there are no
                records, supports France Carte Vitale + Monaco CCSS in the
                same table. Multiple records per system are allowed. */}
            {healthRecords && healthRecords.length > 0 && (
              <Card data-testid="profile-health-section">
                <button
                  onClick={() => toggleSection("health")}
                  className="w-full"
                  data-testid="section-health-toggle"
                >
                  <CardHeader className="flex flex-row items-center justify-between py-4">
                    <div className="flex items-center gap-2">
                      <FileCheck className="h-5 w-5 text-primary" />
                      <CardTitle className="text-base">{t("crewPortal.profileView.health.title")}</CardTitle>
                      <Badge
                        variant="outline"
                        className="ml-1 text-[11px] py-0 px-1.5"
                        data-testid="profile-health-count"
                      >
                        {healthRecords.length}
                      </Badge>
                    </div>
                    {expandedSections.health ? (
                      <ChevronUp className="h-5 w-5 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-muted-foreground" />
                    )}
                  </CardHeader>
                </button>
                {expandedSections.health && (
                  <CardContent className="pt-0 space-y-3">
                    {healthRecords.map((rec) => {
                      const isFr = (rec.system_type || "").toLowerCase() === "france";
                      return (
                        <div
                          key={rec.id}
                          className="rounded-lg border bg-card/50 p-3 space-y-1.5"
                          data-testid={`health-record-${rec.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-base">
                                {isFr ? "🇫🇷" : "🇲🇨"}
                              </span>
                              <span className="font-semibold text-sm">
                                {isFr ? "France" : "Monaco"}
                              </span>
                            </div>
                            <Badge
                              variant="outline"
                              className="text-[11px] py-0 px-1.5"
                            >
                              {rec.provider || (isFr ? "CPAM" : "CCSS")}
                            </Badge>
                          </div>
                          {rec.insurance_number && (
                            <div className="flex justify-between text-xs">
                              <span className="text-muted-foreground">
                                {t("crewPortal.profileView.health.insuranceNumber")}
                              </span>
                              <span
                                className="font-mono"
                                data-testid={`health-record-${rec.id}-insurance-number`}
                              >
                                {rec.insurance_number}
                              </span>
                            </div>
                          )}
                          {rec.card_number && rec.card_number !== rec.insurance_number && (
                            <div className="flex justify-between text-xs">
                              <span className="text-muted-foreground">{t("crewPortal.profileView.health.cardNumber")}</span>
                              <span className="font-mono">{rec.card_number}</span>
                            </div>
                          )}
                          {rec.valid_to && (
                            <div className="flex justify-between text-xs">
                              <span className="text-muted-foreground">{t("crewPortal.profileView.health.validUntil")}</span>
                              <span>{rec.valid_to}</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    <p className="text-xs text-muted-foreground">
                      {t("crewPortal.profileView.health.footnote")}
                    </p>
                  </CardContent>
                )}
              </Card>
            )}
          </div>
        )}
        
        {/* Documents View */}
        {activeView === "documents" && (
          <div data-testid="documents-view">
            {/* EVENTSFLO-CREW-PWA-DOCUMENT-UPLOAD-MVP-V1:
                Authenticated crew (already-onboarded, JWT carries staff_id)
                use the new self-upload panel. Onboarding candidates (no
                staff_id yet) keep the existing CrewDocumentsPanel which
                talks to /api/crew/onboarding/* routes. */}
            {user?.staff_id ? (
              <CrewSelfDocumentsPanel data-testid="crew-self-documents-panel" />
            ) : (
              <CrewDocumentsPanel data-testid="crew-documents-panel" />
            )}
          </div>
        )}
        
        {/* Install App View - EF-PWA-BRANDING-V1-STEP4: Pass companyInfo */}
        {activeView === "install" && <InstallAppView companyInfo={companyInfo} />}
        
        {/* About View - EF-PWA-BRANDING-V1-STEP4: Pass companyInfo */}
        {activeView === "about" && (
          <AboutView theme={theme} onThemeToggle={handleThemeToggle} companyInfo={companyInfo} />
        )}
      </main>
      
      {/* EF-CREW-PWA-NAV-UX-REFINEMENT-V1: Simplified Bottom Navigation (Shifts + Calendar only) */}
      <nav className="fixed bottom-0 left-0 right-0 border-t bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/60 z-30 safe-area-pb">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center gap-12 py-3">
            <button
              onClick={() => handleNavigate("shifts")}
              className={`flex flex-col items-center gap-1.5 px-6 py-2 rounded-xl transition-all ${
                activeView === "shifts" 
                  ? "text-primary bg-primary/10" 
                  : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid="bottom-nav-shifts"
            >
              <Briefcase className={`h-6 w-6 ${activeView === "shifts" ? "scale-110" : ""} transition-transform`} />
              <span className="text-xs font-medium">{t("crewPortal.nav.shifts")}</span>
            </button>
            <button
              onClick={() => handleNavigate("calendar")}
              className={`flex flex-col items-center gap-1.5 px-6 py-2 rounded-xl transition-all ${
                activeView === "calendar" 
                  ? "text-primary bg-primary/10" 
                  : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid="bottom-nav-calendar"
            >
              <Calendar className={`h-6 w-6 ${activeView === "calendar" ? "scale-110" : ""} transition-transform`} />
              <span className="text-xs font-medium">{t("crewPortal.nav.calendar")}</span>
            </button>
          </div>
        </div>
      </nav>

      {/* EVENTSFLO-CREW-PWA-ACTIVE-ROUTE-AND-UI-SPLIT-AUDIT-FIX-V1: Profile update request dialog */}
      <Dialog
        open={profileRequestOpen}
        onOpenChange={(v) => !profileRequestSending && setProfileRequestOpen(v)}
      >
        <DialogContent data-testid="portal-profile-request-dialog">
          <DialogHeader>
            <DialogTitle>{t("crewPortal.profileView.requestDialog.title")}</DialogTitle>
            <DialogDescription>
              {t("crewPortal.profileView.requestDialog.description")}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={profileRequestMessage}
            onChange={(e) => setProfileRequestMessage(e.target.value)}
            rows={5}
            maxLength={2000}
            placeholder={t("crewPortal.profileView.requestDialog.placeholder")}
            data-testid="portal-profile-request-message"
          />
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={profileRequestSending}
              onClick={() => setProfileRequestOpen(false)}
              data-testid="portal-profile-request-cancel"
            >
              {t("crewPortal.profileView.requestDialog.cancel")}
            </Button>
            <Button
              type="button"
              onClick={handleSubmitProfileUpdateRequest}
              disabled={profileRequestSending}
              data-testid="portal-profile-request-submit"
            >
              {profileRequestSending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              {t("crewPortal.profileView.requestDialog.sendRequest")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
